import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CoachingNote } from "@/lib/types";
import {
  loadDrawing,
  type TelestratorObject,
} from "@/lib/canvas/telestrator-schema";
import { LEGACY_DEFAULT_COLOR } from "@/lib/canvas/shared-shape";
import { FeedbackMediaThumbnail } from "./FeedbackMediaThumbnail";

export type VideoNoteThumbnailProps = {
  note: Pick<CoachingNote, "id" | "drawing">;
  className: string;
  testId: string;
  placeholderTestId: string;
  overlayTestId?: string;
  imgClassName?: string;
  /** Full-size player surfaces mirror the coach editor; compact
   * list/rail thumbnails scale fixed glyphs down so the preview
   * stays legible instead of being dominated by controls. */
  overlayScale?: ThumbnailOverlayScale;
};

/** Static marked-frame preview for a video note.
 *
 * A Mark Moment note is not a clip: it represents one saved frame
 * plus the coach's telestration drawing. The server thumbnail gives
 * us the access-controlled video frame; this component paints the
 * persisted drawing on top so every surface shows the same evidence.
 */
export function VideoNoteThumbnail({
  note,
  className,
  testId,
  placeholderTestId,
  overlayTestId = "video-note-drawing-overlay",
  imgClassName,
  overlayScale,
}: VideoNoteThumbnailProps) {
  const drawingObjects = useMemo(
    () => loadDrawing(note.drawing).objects,
    [note.drawing],
  );
  return (
    <FeedbackMediaThumbnail
      kind="note"
      id={note.id}
      testId={testId}
      placeholderTestId={placeholderTestId}
      className={className}
      imgClassName={imgClassName}
    >
      {drawingObjects.length > 0 ? (
        <ThumbnailDrawingOverlay
          overlayId={`note-${note.id}`}
          objects={drawingObjects}
          testId={overlayTestId}
          scale={overlayScale}
        />
      ) : null}
    </FeedbackMediaThumbnail>
  );
}

export type ThumbnailOverlayScale = "compact" | "full";

const DEFAULT_OVERLAY_DIMS = { width: 1600, height: 900 };
const COMPACT_OVERLAY_BASE_DIMS = { width: 320, height: 180 };
const FORMATION_ANCHOR_RADIUS = 10;
const LABEL_PADDING = 6;

export function ThumbnailDrawingOverlay({
  overlayId,
  objects,
  testId,
  scale = "compact",
}: {
  overlayId: string;
  objects: TelestratorObject[];
  testId: string;
  scale?: ThumbnailOverlayScale;
}) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [dims, setDims] = useState(DEFAULT_OVERLAY_DIMS);
  const setRef = useCallback((el: HTMLDivElement | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (!node) return;
    const update = (width: number, height: number) => {
      const nextWidth = Math.max(1, Math.floor(width));
      const nextHeight = Math.max(1, Math.floor(height));
      setDims((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight },
      );
    };
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) update(rect.width, rect.height);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        update(entry.contentRect.width, entry.contentRect.height);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  if (objects.length === 0) return null;
  const arrowId = `video-thumb-arrow-${overlayId}`;
  const glyphScale = overlayGlyphScale(dims, scale);
  return (
    <div ref={setRef} className="pointer-events-none absolute inset-0">
      <svg
        className="h-full w-full"
        viewBox={`0 0 ${dims.width} ${dims.height}`}
        preserveAspectRatio="none"
        data-testid={testId}
        aria-hidden="true"
      >
        <defs>
          <marker
            id={arrowId}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L6,3 L0,6 Z" fill="context-stroke" />
          </marker>
        </defs>
        {objects.map((object, index) =>
          renderThumbnailDrawingObject(
            object,
            `${overlayId}-${index}`,
            arrowId,
            dims,
            glyphScale,
          ),
        )}
      </svg>
    </div>
  );
}

function renderThumbnailDrawingObject(
  object: TelestratorObject,
  key: string,
  arrowId: string,
  dims: { width: number; height: number },
  glyphScale: number,
): ReactNode {
  const stroke =
    "color" in object && object.color ? object.color : LEGACY_DEFAULT_COLOR;
  const rawStrokeWidth =
    "strokeWidth" in object && typeof object.strokeWidth === "number"
      ? object.strokeWidth
      : 3;
  const strokeWidth = Math.max(2, Math.min(5, rawStrokeWidth));
  const common = {
    stroke,
    strokeWidth,
    vectorEffect: "non-scaling-stroke" as const,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  switch (object.kind) {
    case "arrow":
    case "line":
      return (
        <line
          key={key}
          x1={scaleX(object.x1, dims)}
          y1={scaleY(object.y1, dims)}
          x2={scaleX(object.x2, dims)}
          y2={scaleY(object.y2, dims)}
          fill="none"
          markerEnd={object.kind === "arrow" ? `url(#${arrowId})` : undefined}
          {...common}
        />
      );
    case "freehand": {
      const points = pointsToSvg(object.points, dims);
      if (!points) return null;
      return <polyline key={key} points={points} fill="none" {...common} />;
    }
    case "zone":
      return (
        <rect
          key={key}
          x={scaleX(object.x, dims)}
          y={scaleY(object.y, dims)}
          width={scaleX(object.w, dims)}
          height={scaleY(object.h, dims)}
          fill={stroke}
          fillOpacity={0.18}
          strokeDasharray={`${Math.max(8, strokeWidth * 3)} ${Math.max(
            6,
            strokeWidth * 2,
          )}`}
          {...common}
        />
      );
    case "circle": {
      const cx = scaleX(object.x + object.w / 2, dims);
      const cy = scaleY(object.y + object.h / 2, dims);
      const rx = Math.max(4 * glyphScale, scaleX(object.w / 2, dims));
      const ry = Math.max(4 * glyphScale, scaleY(object.h / 2, dims));
      return (
        <ellipse
          key={key}
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          fill="none"
          {...common}
        />
      );
    }
    case "spotlight": {
      const cx = scaleX(object.x + object.w / 2, dims);
      const cy = scaleY(object.y + object.h / 2, dims);
      const minRadius = 8 * glyphScale;
      const radius = Math.max(
        minRadius,
        Math.min(scaleX(object.w / 2, dims), scaleY(object.h / 2, dims)),
      );
      const maskId = `video-thumb-spotlight-mask-${key}`;
      return (
        <g key={key}>
          <defs>
            <mask id={maskId}>
              <rect
                x="0"
                y="0"
                width={dims.width}
                height={dims.height}
                fill="#fff"
              />
              <circle cx={cx} cy={cy} r={radius} fill="#000" />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width={dims.width}
            height={dims.height}
            fill="rgba(0, 0, 0, 0.48)"
            mask={`url(#${maskId})`}
          />
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={stroke}
            strokeWidth={rawStrokeWidth}
            vectorEffect="non-scaling-stroke"
          />
        </g>
      );
    }
    case "label": {
      const x = scaleX(object.x, dims);
      const y = scaleY(object.y, dims);
      const labelPadding = LABEL_PADDING * glyphScale;
      const fontSize = 14 * glyphScale;
      const labelHeight = 18 * glyphScale;
      const approxTextWidth = Math.max(
        40 * glyphScale,
        object.text.length * 8 * glyphScale,
      );
      return (
        <g key={key}>
          <rect
            x={x - labelPadding}
            y={y - labelPadding}
            width={approxTextWidth + labelPadding * 2}
            height={labelHeight + labelPadding * 2}
            rx={6 * glyphScale}
            fill="rgba(15, 23, 42, 0.78)"
            stroke={stroke}
            strokeWidth={Math.max(1, strokeWidth - 1)}
            vectorEffect="non-scaling-stroke"
          />
          <text
            x={x}
            y={y + fontSize}
            fill={stroke}
            fontSize={fontSize}
            fontWeight="700"
            fontFamily="ui-sans-serif, system-ui, sans-serif"
          >
            {object.text}
          </text>
        </g>
      );
    }
    case "formation": {
      const hull = pointsToSvg(object.hull_points, dims);
      return (
        <g key={key}>
          {hull ? (
            <polygon
              points={hull}
              fill={stroke}
              fillOpacity={0.16}
              {...common}
              strokeDasharray="8 5"
            />
          ) : null}
          {object.anchors.map((anchor, index) => (
            <circle
              key={`${key}-anchor-${index}`}
              cx={scaleX(anchor.x, dims)}
              cy={scaleY(anchor.y, dims)}
              r={FORMATION_ANCHOR_RADIUS * glyphScale}
              fill={stroke}
              stroke="rgba(15, 23, 42, 0.6)"
              strokeWidth={1.5 * glyphScale}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </g>
      );
    }
    case "dim":
      return (
        <rect
          key={key}
          x="0"
          y="0"
          width={dims.width}
          height={dims.height}
          fill="#00122c"
          opacity={Math.max(0.18, Math.min(0.45, object.opacity))}
        />
      );
    default:
      return null;
  }
}

function scaleX(value: number, dims: { width: number }): number {
  return value * dims.width;
}

function scaleY(value: number, dims: { height: number }): number {
  return value * dims.height;
}

function overlayGlyphScale(
  dims: { width: number; height: number },
  scale: ThumbnailOverlayScale,
): number {
  if (scale === "full") return 1;
  const widthScale = dims.width / COMPACT_OVERLAY_BASE_DIMS.width;
  const heightScale = dims.height / COMPACT_OVERLAY_BASE_DIMS.height;
  return Math.max(0.2, Math.min(1, widthScale, heightScale));
}

function pointsToSvg(
  points: { x: number; y: number }[],
  dims: { width: number; height: number },
): string | null {
  if (points.length < 2) return null;
  return points
    .map((point) => `${scaleX(point.x, dims)},${scaleY(point.y, dims)}`)
    .join(" ");
}
